/**
 * @file     LessonRenderer.tsx
 * @readme   ./README.md
 * @purpose  Generic lesson renderer, dispatches to the correct lesson type component based on LessonType. Mirrors FlowRenderer pattern.
 */
'use client';

import type { LessonType } from './types';
import { ReadingLesson } from './lessonTypes/ReadingLesson';
import { SlideLesson } from './lessonTypes/SlideLesson';
import { ChecklistLesson } from './lessonTypes/ChecklistLesson';
import { ScenarioLesson } from './lessonTypes/ScenarioLesson';
import type { LessonContent } from '@/content-system/academy/types';

interface LessonRendererProps {
  type: LessonType;
  content: LessonContent;
  onContentConsumed?: () => void;
  onQuizPassed?: () => void;
}

export function LessonRenderer({ type, content, onContentConsumed, onQuizPassed }: LessonRendererProps) {
  // Prefer the content's own type, it knows what data it actually has.
  // The journey config type can be out of sync (e.g. config says 'slide'
  // but content was written as 'scenario').
  const resolvedType = content.type ?? type;
  switch (resolvedType) {
    case 'reading':
      return <ReadingLesson content={content} onContentConsumed={onContentConsumed} onQuizPassed={onQuizPassed} />;
    case 'slide':
      return <SlideLesson content={content} onContentConsumed={onContentConsumed} onQuizPassed={onQuizPassed} />;
    case 'checklist':
      return <ChecklistLesson content={content} onContentConsumed={onContentConsumed} onQuizPassed={onQuizPassed} />;
    case 'scenario':
      return <ScenarioLesson content={content} onContentConsumed={onContentConsumed} onQuizPassed={onQuizPassed} />;
    default:
      return <ReadingLesson content={content} onContentConsumed={onContentConsumed} onQuizPassed={onQuizPassed} />;
  }
}
